import React, { useRef } from "react";
import { Select, Typography } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { getOrderTimeStatus, getOrderTimeStatusLabel } from "../utils/cycles";
import { fontWeights, sizes } from "../constant/styles";
import {
  WHITE,
  BLACK,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  PRIMARY_900,
  SUCCESS_100,
  SUCCESS_800,
  ERROR_100,
  ERROR_800,
  GRAY_100
} from "../constant/colors";

const { Text } = Typography;

const GlobalScrollbarStyle = createGlobalStyle`
  .order-switching-dropdown-menu .rc-virtual-list-holder {
    overflow: auto !important;
  }

  .order-switching-dropdown-menu .rc-virtual-list-holder::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
   background-color: ${GRAY_100};
  }

  .order-switching-dropdown-menu .rc-virtual-list-holder::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: ${GRAY_300};
  }

  .order-switching-dropdown-menu .rc-virtual-list-holder::-webkit-scrollbar-track {
    background-color: ${GRAY_100};
  }
`;

const StyledSelect = styled(Select)`
  min-width: 280px;
  width: 100%;
  cursor: pointer;

  .ant-select-selector {
    border: none !important;
    border-bottom: 1px solid ${GRAY_200} !important;
    border-radius: 0 !important;
    padding: 0 24px 0 12px !important;
    background: ${(props) => props.bgcolor || WHITE} !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
    cursor: pointer !important;
  }

  &.ant-select-open .ant-select-selector {
    border: 1px solid ${PRIMARY_900} !important;
    border-radius: 4px !important;
    box-shadow: 0px 0px 3px 0px rgba(78, 112, 167, 1) !important;
  }

  .ant-select-selection-search-input {
    cursor: text !important;
  }

  .ant-select-selection-item {
    padding: 0 !important;
    line-height: normal !important;
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    font-weight: ${fontWeights.semibold} !important;

    .ant-typography {
      font-weight: ${fontWeights.semibold} !important;
    }
  }

  .ant-select-selection-item span:last-child {
    margin-right: 18px !important;
  }

  .ant-select-arrow {
    color: ${BLACK};
  }

  &.ant-select-open .ant-select-arrow {
    color: ${GRAY_500};
  }

  .ant-select-item,
  .ant-select-item-option {
    padding: 0px 12px !important;
    cursor: pointer !important;
  }

  .ant-select-item-option-content {
    cursor: pointer !important;
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    font-weight: ${fontWeights.semibold} !important;

    .ant-typography {
      font-weight: ${fontWeights.semibold} !important;
    }
  }

  &.ant-select-disabled {
    cursor: not-allowed;

    .ant-select-selector {
      cursor: not-allowed !important;
    }
  }
`;

const StatusBadge = styled.span`
  padding: 5px 10px;
  border-radius: 16px;
  font-weight: ${fontWeights.bold};
  font-size: ${sizes.small};
  line-height: ${sizes.small};
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;

  &.status-current {
    background-color: ${SUCCESS_100};
    color: ${SUCCESS_800};
  }

  &.status-past {
    background-color: ${ERROR_100};
    color: ${ERROR_800};
  }

  &.status-future {
    background-color: ${GRAY_200};
    color: ${BLACK};
  }
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 8px 0;
  gap: 8px;
  flex-wrap: nowrap;
`;

const OptionText = styled(Text)`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.medium};
  line-height: 21px;
  color: ${BLACK};
  flex: 0 0 auto;
  min-width: 180px;
  white-space: nowrap;

  .ant-select-item-option-selected & {
    font-weight: ${fontWeights.semibold};
  }

  .ant-select-selection-item & {
    font-weight: ${fontWeights.semibold};
  }

  .ant-select-open .ant-select-selection-item & {
    color: ${GRAY_400};
  }
`;

const OrderSwitchingDropdown = ({
  orders = [],
  selectedOrderId,
  currentOrderIndex,
  onOrderChange,
  disabled = false,
  valueField = "id",
  placeholder,
  dataTMId,
  backgroundColor = WHITE
}) => {
  const { t } = useTranslation();
  const selectRef = useRef(null);

  const totalOrders = orders.length;

  // Handle order selection change
  const handleChange = (orderId) => {
    if (onOrderChange && typeof onOrderChange === "function") {
      onOrderChange(orderId);
    }
    // Blur the select to remove cursor after selection
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  // Handle blur event to ensure dropdown closes and cursor disappears
  const handleBlur = () => {
    if (selectRef.current) {
      selectRef.current.blur();
    }
  };

  const getOrderSearchText = (order) => {
    const orderNumber = order.orderIndex + 1;
    const adminDate = moment(order.administrationDate).format("MM/DD/YYYY");
    const status = getOrderTimeStatus(order.orderIndex, currentOrderIndex);
    const statusLabel = getOrderTimeStatusLabel(status.key, t);
    const orderLabel = t("patient.order.label_format", { orderNumber, totalOrders, adminDate });

    return `${orderLabel} ${statusLabel}`.toLowerCase();
  };

  // Custom filter function for search
  const handleFilterOption = (input, option) => {
    const order = orders.find((o) => {
      const orderValue = valueField === "cycleNumber" ? o.orderIndex + 1 : o.id;
      return orderValue === option.value;
    });

    if (!order) return false;

    const searchText = getOrderSearchText(order);
    return searchText.includes(input.toLowerCase());
  };

  // Render option for dropdown list
  const renderOption = (order) => {
    const orderNumber = order.orderIndex + 1;
    const adminDate = moment(order.administrationDate).format("MM/DD/YYYY");
    const status = getOrderTimeStatus(order.orderIndex, currentOrderIndex);
    const statusLabel = getOrderTimeStatusLabel(status.key, t);

    return (
      <OptionContainer>
        <OptionText>{t("patient.order.label_format", { orderNumber, totalOrders, adminDate })}</OptionText>
        <StatusBadge className={`status-${status.key}`}>{statusLabel}</StatusBadge>
      </OptionContainer>
    );
  };

  // Early return if no orders
  if (!orders || orders.length === 0) {
    return null;
  }

  return (
    <div id="patient-order-switching-dropdown-container">
      <GlobalScrollbarStyle />
      <StyledSelect
        id="patient-order-switching-dropdown"
        ref={selectRef}
        value={selectedOrderId}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        dropdownMatchSelectWidth={false}
        dropdownClassName="order-switching-dropdown-menu"
        listHeight={230}
        dropdownStyle={{ maxHeight: "230px" }}
        placement="bottomRight"
        getPopupContainer={(trigger) => trigger.parentNode}
        aria-label={t("patient.order.switch_order_dropdown_label")}
        placeholder={placeholder}
        showSearch={true}
        filterOption={handleFilterOption}
        data-tm-id={dataTMId}
        bgcolor={backgroundColor}
      >
        {orders.map((order) => {
          const orderValue = valueField === "cycleNumber" ? order.orderIndex + 1 : order.id;
          const orderNumber = order.orderIndex + 1;

          return (
            <Select.Option key={order.id} value={orderValue} id={`patient-order-${orderNumber}-option`}>
              {renderOption(order)}
            </Select.Option>
          );
        })}
      </StyledSelect>
    </div>
  );
};

export default OrderSwitchingDropdown;
